import React, { useState, useEffect } from "react";
import { Button } from "./Button";
import { ReactComponent as CarIcon } from "../assets/svg/car.svg";
import { ReactComponent as HomeIcon } from "../assets/svg/home.svg";
import { ReactComponent as EventIcon } from "../assets/svg/event.svg";
import { ReactComponent as BeachIcon } from "../assets/svg/beach.svg";
import { ReactComponent as MoneyBagIcon } from "../assets/svg/moneybag.svg";
import { ReactComponent as PiggyIcon } from "../assets/svg/piggy.svg";
import { ReactComponent as LampIcon } from "../assets/svg/lamp.svg";
import { ReactComponent as ArrowRightIcon } from "../assets/svg/arrowRight.svg";
import { ReactComponent as InfoIcon } from "../assets/svg/info.svg";
import rightArrow from "../assets/svg/right-arrow.svg";
import loaderGif from "../assets/gifs/loader.gif";
import axios from "axios";
import { encrypt } from "../utils/encrypt-decrypt";
import { useNavigate } from "react-router-dom";
import useLocale from "../locales";
import Tooltip from "./Tooltip";
import { ReactComponent as GoalPlannerIcon } from "../assets/svg/goalplanner.svg";
import VehicleCalculator from "./VehicleCalculator";
import HomeCalculator from "./HomeCalculator";
import WeddingCalculator from "./WeddingCalculator";
import VacationCalculator from "./VacationCalculator";
import WealthCalculator from "./WealthCalculator";
import OtherGoalCalculator from "./OtherGoalCalculator";

const calculatorMap = {
  "buy-a-vehicle": VehicleCalculator,
  "buy-a-home": HomeCalculator,
  "wedding-event": WeddingCalculator,
  vacation: VacationCalculator,
  "build-wealth": WealthCalculator,
  "other-goal": OtherGoalCalculator,
};

// Card Component (exported for inline display)
export const GoalPlannerCard = ({ onOpen }) => {
  const { textData } = useLocale();

  return (
    <div className="bg-light-sky !z-0 flex justify-between items-start md:items-center flex-col md:flex-row rounded-2xl p-3 border border-sky-blue relative overflow-hidden">
      <div className="flex flex-col sm:flex-col gap-[12px] max-w-[488px]">
        <div className=" bg-gradient-primary w-fit text-white px-3 rounded-[4px]">
          <span className="text-[14px] leading-[100%] font-lato-medium-500">⚡{" "}{textData.goalPlanner.new || "⚡ New"}</span>
        </div>

        <h3 className="text-[18px] font-lato-bold-700 text-[#282828] leading-[150%]">
          {textData.goalPlanner.meetYour} <span className="text-[#0D8787]">{textData.goalPlanner.goalFinance}</span>
        </h3>
        <div className="flex flex-col gap-3">
          <p className="text-[14px] leading-[140%] text-gray-font font-lato-regular-400">
            {textData.goalPlanner.goalDesc || "Home, wedding, vacation, wealth - whatever your goal, our AI builds the strategy to achieve it."}
          </p>
          <Button
            onClick={onOpen}
            type="button"
            className="w-fit sm:!h-[40px] font-lato-semibold-600 rounded-[178px] !text-base sm:!text-[16px] font-semibold px-4 md:px-4 py-[11px] flex gap-1"
            disabled={false}
            textClassName="text-[16px] !px-0"
          >
            <span className="text-[16px] pr-1">{textData.goalPlanner.goalBtn || "Plan a Goal Now"}</span>
            <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.5 6.34766H0.5M15.5 6.34766L10.5 1.34766M15.5 6.34766L10.5 11.3477" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </Button>
        </div>
      </div>
      <div className="flex-shrink-0 ">
        <GoalPlannerIcon className="h-[168px] size-full" />
      </div>
    </div>
  );
};

const GoalPLanner = ({ isOpen: externalIsOpen, onClose: externalOnClose }) => {
  const navigate = useNavigate();
  const { textData } = useLocale();
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const isModalOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;

  const handleOpen = () => {
    if (externalIsOpen === undefined) {
      setInternalIsOpen(true);
    }
  };

  const handleClose = () => {
    if (externalOnClose) {
      externalOnClose();
    } else {
      setInternalIsOpen(false);
    }
  };

  const [currentStep, setCurrentStep] = useState("selection");
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [calculationResult, setCalculationResult] = useState(null);
  const [savedFormState, setSavedFormState] = useState(null);

  const calcNames = {
    "personal-loan-calculator": textData.filterDropDown.personalLoanCalc,
    "fixed-deposit-calculator": textData.filterDropDown.FDCalc,
    "recurring-deposit-calculator": textData.filterDropDown.RDCalc,
    "debt-to-income-ratio-calculator": textData.filterDropDown.DTICalc,
    "net-worth-calculator": textData.filterDropDown.netWorthCalc,
    "loan-payoff-calculator": textData.filterDropDown.earlyLoanCalc,
    "student-education-loan-calculator": textData.filterDropDown.educationLoanCalc,
    "credit-card-payoff-calculator": textData.filterDropDown.creditCardPayoffCalc,
    "business-loan-calculator": textData.filterDropDown.businessLoanCalc,
    "salary-calculator": textData.filterDropDown.salaryCalc,
    "pension-calculator": textData.filterDropDown.pensionCalc,
    "car-lease-calculator": textData.filterDropDown.carCalc,
    "mortgage-loan-refinance-calculator": textData.filterDropDown.mortgageCalc,
    "401k-retirement-calculator": textData.filterDropDown.contributionCalc,
    "saving-goal-calculator": textData.filterDropDown.savingCalc,
    "loan-to-value-calculator": textData.filterDropDown.loanToValueCalc,
    "sip-investment-calculator": textData.filterDropDown.sipCalc,
  };

  const goals = [
    {
      name: textData.goalPlanner.vehicle,
      icon: CarIcon,
      id: "buy-a-vehicle",
    },
    {
      name: textData.goalPlanner.home,
      icon: HomeIcon,
      id: "buy-a-home",
    },
    {
      name: textData.goalPlanner.wedding,
      icon: EventIcon,
      id: "wedding-event",
    },
    {
      name: textData.goalPlanner.vacation,
      icon: BeachIcon,
      id: "vacation",
    },
    {
      name: textData.goalPlanner.wealth,
      icon: MoneyBagIcon,
      id: "build-wealth",
    },
    {
      name: textData.goalPlanner.other,
      icon: PiggyIcon,
      id: "other-goal",
    },
  ];

  const resetModalState = () => {
    setCurrentStep("selection");
    setSelectedGoal(null);
    setSavedFormState(null);
    setCalculationResult(null);
    setError(false);
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (externalIsOpen !== undefined && externalIsOpen) {
      resetModalState();
    }
  }, [externalIsOpen]);

  const closeModal = () => {
    handleClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const handleGoalSelect = (goal) => {
    setSelectedGoal(goal);
    setSavedFormState(null);
    setCurrentStep("input");
  };

  const calculateGoal = async ({ payload, calculationResult: localResult, values, timeUnit }) => {
    try {
      setLoading(true);
      setError(false);
      setSavedFormState({ values, timeUnit });
      setCurrentStep("result");

      console.log("=-=-=-", payload);
      console.log("iv_key", process.env.REACT_APP_GROWLIFE_CRYPTO_IV_KEY);
      console.log("secret_key", process.env.REACT_APP_GROWLIFE_CRYPTO_SECRET_KEY);
      console.log("algorithm", process.env.REACT_APP_GROWLIFE_CRYPTO_ALGORITHM);
      const encryptedData = encrypt(
        {
          params: payload,
          iv_key: process.env.REACT_APP_GROWLIFE_CRYPTO_IV_KEY,
          secret_key: process.env.REACT_APP_GROWLIFE_CRYPTO_SECRET_KEY,
        },
        process.env.REACT_APP_GROWLIFE_CRYPTO_SECRET_KEY,
        process.env.REACT_APP_GROWLIFE_CRYPTO_IV,
        process.env.REACT_APP_GROWLIFE_CRYPTO_ALGORITHM
      );

      const response = await axios.post(process.env.REACT_APP_AI_BASE_URL + "/goal-planner", {
        type: "GROWLIFE",
        token: encryptedData,
      });

      setCalculationResult({
        ...localResult,
        apiResponse: response.data?.data || null,
      });
    } catch (error) {
      console.error("Error calculating goal:", error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyClass = (difficulty) => {
    if (!difficulty) return "bg-[#F6F8FB] text-[#6592CB] border border-[#6592CB]";
    switch (difficulty.toLowerCase()) {
      case "easy":
        return "bg-[#E8F9E8] text-[#44A91F] border border-[#44A91F]";
      case "medium":
        return "bg-[#FFF6E3] text-[#F5A623] border border-[#F5A623]";
      case "hard":
        return "bg-[#F7E6EB] text-[#A80132] border border-[#A80132]";
      case "impossible":
        return "bg-[#FFE5E5] text-[#ff0000] border border-[#ff0000]";
      default:
        return "bg-[#F6F8FB] text-[#6592CB] border border-[#6592CB]";
    }
  };

  const ActiveCalculator = selectedGoal ? calculatorMap[selectedGoal.id] : null;

  return (
    <>
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm flex justify-center items-center z-50"
          // onClick={handleBackdropClick}
        >
          <div
            className={`bg-white rounded-2xl shadow-2xl w-full max-w-[95%] mx-0 sm:mx-4 ${
              currentStep === "result" && !loading ? "md:max-w-[800px]" : "md:max-w-[500px]"
            } max-h-[90vh] overflow-y-auto relative animate-fadeIn flex flex-col gap-3`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 pb-3 border-b border-[#DDE1E5]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-base text-[#282828] font-lato-semibold-600">
                    {currentStep === "selection" && textData.goalPlanner.title}
                    {currentStep === "input" && selectedGoal?.name}
                    {currentStep === "result" && textData.goalPlanner.title}
                  </h2>
                </div>

                <button
                  onClick={closeModal}
                  className="w-8 h-8 bg-[#DDE1E5] flex items-center justify-center rounded-full hover:bg-gray-300 transition-colors"
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M1 1L9 9M1 9L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 pt-0">
              <div className="min-h-[300px]">
                {currentStep === "selection" && (
                  <div className="w-full space-y-4">
                    <div className="grid xs:grid-cols-2 grid-cols-1 gap-3">
                      {goals.map((goal, index) => (
                        <div
                          onClick={() => handleGoalSelect(goal)}
                          key={goal.id}
                          className={`flex flex-col cursor-pointer rounded-xl p-[14px] ${
                            index % 2 === 0
                              ? "bg-[#E8FFFE] border border-sky-blue"
                              : "bg-[#EDFFF1] border-dark-green border"
                          }`}
                        >
                          <div className="flex items-center justify-between w-full">
                            <goal.icon className="size-8" />
                            <ArrowRightIcon />
                          </div>
                          <p className="text-md leading-[1.5] font-semibold text-start text-[#282828] font-lato-semibold-600 mt-10">
                            {goal.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {currentStep === "input" && ActiveCalculator && (
                  <ActiveCalculator
                    selectedGoal={selectedGoal}
                    onSubmit={calculateGoal}
                    initialValues={savedFormState?.values}
                    initialTimeUnit={savedFormState?.timeUnit}
                  />
                )}

                {currentStep === "result" &&
                  (loading ? (
                    <div className="w-full min-h-[300px] flex items-center justify-center">
                      <div className="text-center flex flex-col gap-8">
                        <img src={loaderGif} alt="loader" className="mx-auto" />
                        <div
                          className="text-[24px] font-lato-regular-400"
                          style={{
                            background:
                              "linear-gradient(88.83deg, #E56489 -2.77%, #AE1595 30.94%, #5230EE 64.9%, #6B55F0 92.76%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                        >
                          {textData.goalPlanner.thinking}
                        </div>
                      </div>
                    </div>
                  ) : error ? (
                    <div className="w-full min-h-[300px] flex items-center justify-center">
                      <div className="text-center space-y-4">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <p className="text-lg text-gray-700 font-lato-regular-400">
                          {textData.goalPlanner.errorCalculating}
                        </p>
                        <Button
                          onClick={() => {
                            setError(false);
                            setCurrentStep("input");
                          }}
                          className="w-full rounded-full font-lato-semibold-600 text-base transition-colors mt-4"
                        >
                          {textData.goalPlanner.tryAgain}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    calculationResult && (
                      <div className="w-full space-y-4">
                        <div className="bg-[#F6F8FB] rounded-xl py-4 px-3 border border-[#DDE1E5]">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-lato-bold-700 text-[#282828]">
                              {calculationResult.apiResponse?.strategy_title || textData.goalPlanner.strategyTitleDefault}
                            </h3>
                            <Tooltip
                              content={textData.goalPlanner.difficultyTooltip || "Difficulty of the goal."}
                              placement="bottom"
                            >
                              <span
                                className={`px-3 py-1 rounded-full text-base font-lato-semibold-600 ${getDifficultyClass(
                                  calculationResult.apiResponse?.difficulty
                                )}`}
                              >
                                {textData.goalPlanner[calculationResult.apiResponse?.difficulty?.toLowerCase()] ||
                                  calculationResult.apiResponse?.difficulty ||
                                  textData.goalPlanner.hard}
                              </span>
                            </Tooltip>
                          </div>

                          <div className="text-center py-3 flex flex-col gap-3 bg-white rounded-md">
                            <p className="text-base text-gray-font font-lato-regular-400 mb-2">
                              {textData.goalPlanner.mothlySavingNeed}
                            </p>
                            <div className="text-base sm:text-[20px] md:text-[22px] font-lato-bold-700 text-[#07B5B5]">
                              {calculationResult.apiResponse?.monthly_savings_needed
                                ? calculationResult.apiResponse.monthly_savings_needed.toLocaleString()
                                : calculationResult.requiredMonthly.toFixed(0).toLocaleString()}
                            </div>
                          </div>
                        </div>

                        {calculationResult.apiResponse?.recommendation && (
                          <div className="bg-[#E8F8E8] border-2 border-[#44A91F] rounded-xl p-4 space-y-3">
                            <div className="inline-flex items-center gap-2 bg-[#44A91F] text-white px-3 py-1.5 rounded-full">
                              <LampIcon className="size-4" />
                              <span className="text-base font-lato-semibold-600">{textData.goalPlanner.recommendation}</span>
                            </div>
                            <div
                              className="text-[#282828] text-sm font-lato-regular-400 leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: calculationResult.apiResponse.recommendation }}
                            />
                          </div>
                        )}

                        <div className="flex items-start gap-2 text-xs text-gray-500 px-3 py-2.5 bg-[#F6F8FB] rounded-lg">
                          <InfoIcon className="shrink-0" />
                          <p className="leading-[120%] text-gray-font text-sm font-lato-regular-400">
                            {textData.goalPlanner.disclaimer}
                          </p>
                        </div>

                        {calcNames[calculationResult.apiResponse?.recommended_calculator_id] ? (
                          <div className="pt-2 bordeer border-t border-gray-light">
                            <h4 className="text-base font-lato-semibold-600 text-[#282828] mb-3">
                              {textData.goalPlanner.readyToPlan}
                            </h4>
                            <button
                              onClick={() => {
                                navigate(`/calculator/${calculationResult.apiResponse?.recommended_calculator_id}`);
                              }}
                              className="w-full bg-[#D4F4F4] hover:bg-[#B8EFEF] text-[#282828] py-3.5 px-4 rounded-xl font-lato-semibold-600 text-base transition-colors flex items-center justify-between group"
                            >
                              <span>{calcNames[calculationResult.apiResponse?.recommended_calculator_id]}</span>
                              <img src={rightArrow} alt="Right Arrow" className="w-5 h-5" aria-hidden="true" />
                            </button>
                          </div>
                        ) : null}
                      </div>
                    )
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GoalPLanner;
